import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { queryKeys } from "@/hooks/queryKeys";
import { useUiStore } from "@/stores/ui.store";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Typography } from "@/components/ui/Typography";
import { Card, CardContent } from "@/components/ui/Card";

const CATEGORIES = [
  { value: "MAINTENANCE", label: "Maintenance" },
  { value: "TAX", label: "Tax" },
  { value: "INSURANCE", label: "Insurance" },
  { value: "UTILITY", label: "Utility" },
  { value: "WATER_BILL", label: "Water bill" },
  { value: "OTHER", label: "Other" },
] as const;

const PAID_BY_OPTIONS = [
  { value: "OWNER", label: "Paid by owner (tenant owes)" },
  { value: "TENANT", label: "Paid by tenant (owner owes / reimbursement)" },
] as const;

export function AddExpensePage() {
  const params = useParams();
  const navigate = useNavigate();
  const propertyId = params.id as string;
  const [searchParams] = useSearchParams();
  const preselectedUnitId = searchParams.get("unitId");
  const queryClient = useQueryClient();
  const addToast = useUiStore((s) => s.addToast);

  const [propertyName, setPropertyName] = useState("");
  const [units, setUnits] = useState<{ id: string; alias: string; unitNumber: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [form, setForm] = useState({
    scope: (preselectedUnitId ? "unit" : "common") as "common" | "unit",
    unitId: preselectedUnitId ?? "",
    category: "MAINTENANCE" as const,
    amount: "",
    date: new Date().toISOString().slice(0, 10),
    paidBy: "OWNER" as const,
    notes: "",
  });

  useEffect(() => {
    async function load() {
      try {
        const prop = await api.get<{ name: string; units: { id: string; alias: string; unitNumber: string }[] }>(`/properties/${propertyId}`);
        setPropertyName(prop.name);
        setUnits(prop.units ?? []);
      } catch {
        addToast({ type: "error", message: "Failed to load property" });
        navigate("/properties");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [propertyId, navigate, addToast]);

  useEffect(() => {
    if (preselectedUnitId && units.some((u) => u.id === preselectedUnitId)) {
      setForm((f) => ({ ...f, scope: "unit", unitId: preselectedUnitId }));
    }
  }, [preselectedUnitId, units]);

  function update(field: string, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrors({});

    const amount = parseFloat(form.amount);
    const newErrors: Record<string, string> = {};
    if (!amount || amount <= 0) newErrors.amount = "Valid amount is required";
    if (!form.date) newErrors.date = "Date is required";
    if (form.scope === "unit" && !form.unitId) newErrors.unitId = "Please select a unit";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/expenses", {
        propertyId,
        unitId: form.scope === "unit" && form.unitId ? form.unitId : undefined,
        category: form.category,
        amount,
        date: new Date(form.date).toISOString(),
        paidBy: form.paidBy,
        notes: form.notes.trim() || undefined,
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.propertyDetail(propertyId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardSummary() });
      addToast({ type: "success", message: "Expense added" });
      navigate(`/property/${propertyId}`);
    } catch (err) {
      addToast({
        type: "error",
        message: err instanceof ApiError ? err.message : "Failed to add expense",
      });
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-64 bg-slate-200 dark:bg-slate-700 rounded-lg animate-pulse" />
        <div className="h-64 bg-slate-200 dark:bg-slate-700 rounded-2xl animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <Link
          to={`/property/${propertyId}`}
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-2"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
          </svg>
          Back to {propertyName}
        </Link>
        <Typography variant="h1">Add Expense</Typography>
        <p className="mt-1 text-sm text-slate-500">Record an expense for {propertyName}</p>
      </div>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardContent className="space-y-4">
            <Select
              label="Scope"
              options={[
                { value: "common", label: "Common (property-level)" },
                { value: "unit", label: "Specific unit" },
              ]}
              value={form.scope}
              onChange={(e) => update("scope", e.target.value as "common" | "unit")}
            />
            <Typography variant="helper" as="p" className="mt-1">Common expenses apply to the whole property; unit expenses are for a single unit.</Typography>
            {form.scope === "unit" && (
              <div>
                <Select
                  label="Unit"
                  options={[{ value: "", label: "Select unit" }, ...units.map((u) => ({ value: u.id, label: `${u.alias} (${u.unitNumber})` }))]}
                  value={form.unitId}
                  onChange={(e) => update("unitId", e.target.value)}
                />
                {units.length === 0 && (
                  <p className="mt-1 text-xs text-amber-600">No units in this property. Add units first or use Common.</p>
                )}
              </div>
            )}
            <Select
              label="Category"
              options={CATEGORIES.map((c) => ({ value: c.value, label: c.label }))}
              value={form.category}
              onChange={(e) => update("category", e.target.value)}
            />
            <Input
              label="Amount (₹)"
              type="number"
              min="1"
              step="1"
              value={form.amount}
              onChange={(e) => update("amount", e.target.value)}
              error={errors.amount}
              placeholder="5000"
              required
            />
            <div>
              <Select
                label="Paid by"
                options={PAID_BY_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                value={form.paidBy}
                onChange={(e) => update("paidBy", e.target.value as "OWNER" | "TENANT")}
              />
              <Typography variant="helper" className="mt-1">Who paid this expense; useful for tracking who owes whom.</Typography>
            </div>
            <Input
              label="Date"
              type="date"
              value={form.date}
              onChange={(e) => update("date", e.target.value)}
              error={errors.date}
              required
            />
            <Input
              label="Notes (optional)"
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              placeholder="Description or reference"
            />
          </CardContent>
        </Card>

        <div className="flex gap-3 mt-6">
          <Link to={`/property/${propertyId}`}>
            <Button variant="secondary" type="button">Cancel</Button>
          </Link>
          <Button type="submit" isLoading={submitting}>Add Expense</Button>
        </div>
      </form>
    </div>
  );
}
